import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ChevronLeft, ChevronRight, Sprout, GraduationCap, Trophy, Users,
  Megaphone, Link2, Bell, ExternalLink, FileText, Building2,
} from "lucide-react";
import { useFood } from "./FoodLayout";
import { STATIC_CONTENT } from "./_shared";
import RecentAcademicsWidget from "@/components/RecentAcademicsWidget";
import AlumniStrip from "@/components/AlumniStrip";

function toYoutubeEmbed(url) {
  if (!url) return "";
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    let id = "";
    if (host === "youtu.be") id = u.pathname.split("/").filter(Boolean)[0] || "";
    else if (host.endsWith("youtube.com") || host.endsWith("youtube-nocookie.com")) {
      if (u.pathname === "/watch") id = u.searchParams.get("v") || "";
      else if (u.pathname.startsWith("/embed/")) id = u.pathname.split("/")[2] || "";
      else if (u.pathname.startsWith("/shorts/")) id = u.pathname.split("/")[2] || "";
    }
    return id ? `https://www.youtube.com/embed/${id}?rel=0` : "";
  } catch { return ""; }
}

function Slider({ slides }) {
  const [idx, setIdx] = useState(0);
  const list = (slides || []).length > 0 ? slides : null;
  useEffect(() => {
    if (!list || list.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % list.length), 5000);
    return () => clearInterval(t);
  }, [list]);

  if (!list) {
    return (
      <section className="relative bg-gradient-to-br from-[#1F6B3F] via-[#13532E] to-[#0e3a22] text-white overflow-hidden" data-testid="ft-hero">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute -top-20 right-10 w-80 h-80 rounded-full bg-[#F4751F] blur-3xl" />
          <div className="absolute bottom-0 -left-10 w-96 h-96 rounded-full bg-[#FFD9A8] blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32 text-center">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#FFD9A8] font-semibold inline-flex items-center gap-2">
            <Sprout size={12} strokeWidth={2.5} /> Where food meets science
          </div>
          <h1 className="mt-3 font-serif text-5xl md:text-7xl tracking-tight">
            Engineering the future of food
          </h1>
          <p className="mt-4 text-base md:text-xl text-white/85 max-w-2xl mx-auto">
            From farm to plate — pilot-scale labs, an industry-driven curriculum, and an active innovation ecosystem in the heart of Marathwada.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            <Link to="admissions" className="bg-[#F4751F] hover:bg-[#E36213] text-white font-semibold px-6 py-3 rounded-md inline-flex items-center gap-2">
              Admissions <ArrowRight size={16} strokeWidth={2} />
            </Link>
            <Link to="about" className="bg-white/10 hover:bg-white/20 text-white border border-white/30 px-6 py-3 rounded-md">
              About Us
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative bg-black" data-testid="ft-hero-slider">
      <div className="relative w-full overflow-hidden" style={{ aspectRatio: "1920 / 620" }}>
        {list.map((s, i) => (
          <a
            key={s.id}
            href={s.link_url || undefined}
            target={s.link_url ? "_blank" : undefined}
            rel="noopener noreferrer"
            className={`absolute inset-0 transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          >
            <img src={s.image_url} alt={s.caption || ""} className="w-full h-full object-cover" />
            {s.caption && (
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-6 md:p-12">
                <div className="max-w-7xl mx-auto">
                  <h2 className="font-serif text-white text-2xl md:text-5xl drop-shadow">{s.caption}</h2>
                </div>
              </div>
            )}
          </a>
        ))}
        {list.length > 1 && (
          <>
            <button
              onClick={() => setIdx((i) => (i - 1 + list.length) % list.length)}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/40 hover:bg-[#F4751F] text-white flex items-center justify-center"
              aria-label="Previous"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => setIdx((i) => (i + 1) % list.length)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/40 hover:bg-[#F4751F] text-white flex items-center justify-center"
              aria-label="Next"
            >
              <ChevronRight size={20} />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {list.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  className={`h-2 rounded-full transition-all ${i === idx ? "bg-[#F4751F] w-6" : "bg-white/40 hover:bg-white/70 w-2"}`}
                  aria-label={`slide ${i + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default function Home() {
  const data = useFood();
  const academics = data.academics || [];
  const inst = data.institute || {};
  const admissions = data.notices_admission || [];
  const links = data.links || [];
  const notices = data.notices_general || [];
  const ytEmbed = toYoutubeEmbed(inst.youtube_url);

  const stats = [
    {
      icon: GraduationCap,
      n: inst.year_of_establishment ? `${Math.max(1, new Date().getFullYear() - Number(inst.year_of_establishment))}+` : "10+",
      l: "Years of Excellence",
      color: "from-[#1F6B3F] to-[#13532E]",
    },
    { icon: Users, n: `${(data.departments || []).length || 0}+`, l: "Active Departments", color: "from-[#F4751F] to-[#C95507]" },
    { icon: Building2, n: "MCAER", l: "Approved", color: "from-[#13532E] to-[#0e3a22]" },
    { icon: Trophy, n: "GSM", l: "Flagship Institution", color: "from-[#C95507] to-[#A0440A]" },
  ];

  return (
    <div data-testid="ft-home">
      <Slider slides={data.header_slides} />

      {/* Stat strip */}
      <div className="bg-white border-b border-[#E6E2D6]">
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-5">
          {stats.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.l} className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-md bg-gradient-to-br ${s.color} text-white flex items-center justify-center`}>
                  <Icon size={20} strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-serif text-xl md:text-2xl text-[#0F1F17]">{s.n}</div>
                  <div className="text-xs text-[#4D5A52] uppercase tracking-wide">{s.l}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* About teaser */}
      <section className="py-14 md:py-20 bg-[#FCFAF4]">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-start">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#C95507] font-semibold inline-flex items-center gap-2">
              <Sprout size={11} strokeWidth={2.5} /> Welcome to {inst.name || "MIT Food Tech"}
            </div>
            <h2 className="mt-2 font-serif text-3xl md:text-5xl text-[#0F1F17]">
              Where food meets <span className="text-[#1F6B3F]">science</span>
              {inst.year_of_establishment && (
                <>, <span className="text-[#F4751F]">since {inst.year_of_establishment}</span></>
              )}
            </h2>
            <p className="mt-4 text-[#4D5A52] leading-relaxed">
              {STATIC_CONTENT.about_intro}
            </p>
            <Link
              to="about"
              className="mt-6 inline-flex items-center gap-2 bg-[#1F6B3F] hover:bg-[#13532E] text-white px-5 py-2.5 rounded-md text-sm"
            >
              Read more <ArrowRight size={14} strokeWidth={2} />
            </Link>
          </div>
          <div className="rounded-xl overflow-hidden shadow-xl ring-1 ring-[#E6E2D6] bg-black" data-testid="ft-home-video">
            <div className="relative w-full aspect-video">
              {ytEmbed ? (
                <iframe
                  src={ytEmbed}
                  title={`${inst.name || "Institute"} — Campus Story`}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full border-0"
                  data-testid="ft-home-youtube"
                />
              ) : inst.home_image_url ? (
                <img
                  src={inst.home_image_url}
                  alt={inst.name || "Institute"}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                  data-testid="ft-home-image"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-white/60 text-sm">
                  Video / image will appear here once configured by the institute.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Admissions / Important Links / Notices */}
      <section className="py-14 md:py-20 bg-white" data-testid="ft-home-info-strip">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#C95507] font-semibold">
              Stay Informed
            </div>
            <h2 className="mt-2 font-serif text-3xl md:text-5xl text-[#0F1F17]">Latest Updates</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <InfoColumn
              title="Admissions"
              eyebrow="Join Us"
              icon={Megaphone}
              gradient="from-[#F4751F] to-[#C95507]"
              ringColor="ring-[#FFF1E5]"
              items={admissions}
              empty="Admission notices will appear here once published."
              testid="ft-home-admissions"
            />
            <InfoColumn
              title="Important Links"
              eyebrow="Resources"
              icon={Link2}
              gradient="from-[#1F6B3F] to-[#13532E]"
              ringColor="ring-[#E6F3EB]"
              items={links}
              empty="No important links have been published yet."
              testid="ft-home-links"
            />
            <InfoColumn
              title="Notices"
              eyebrow="Communications"
              icon={Bell}
              gradient="from-[#13532E] to-[#0e3a22]"
              ringColor="ring-[#E6F3EB]"
              items={notices}
              empty="No notices have been published yet."
              testid="ft-home-notices"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-gradient-to-r from-[#F4751F] to-[#C95507] text-white">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <h2 className="font-serif text-3xl md:text-4xl">Admissions Open</h2>
            <p className="mt-1 text-white/95">Food Technology · Dairy · Processing Engineering</p>
            {inst.approval_bodies && (
              <p className="mt-1 text-white/85 text-sm">{inst.approval_bodies}</p>
            )}
          </div>
          <Link
            to="departments"
            className="bg-[#0F1F17] hover:bg-[#1F6B3F] text-white px-6 py-3 rounded-md inline-flex items-center gap-2 font-medium"
          >
            Explore Departments <ArrowRight size={16} strokeWidth={2} />
          </Link>
        </div>
      </section>
    <RecentAcademicsWidget docs={academics} theme="orange" testidPrefix="ft-ac" linkTo="academics" />
    <AlumniStrip items={(data.alumni || []).filter((a) => a.featured)} theme="light" testid="ft-alumni" registerHref="/foodtech/alumni/register" />
    </div>
  );
}

function InfoColumn({ title, eyebrow, icon: Icon, gradient, ringColor, items, empty, testid }) {
  const list = (items || []).slice(0, 8);
  const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const isNew = (it) => {
    const ts = it.created_at ? new Date(it.created_at).getTime() : NaN;
    return Number.isFinite(ts) && ts >= sevenDaysAgo;
  };
  return (
    <div
      data-testid={testid}
      className={`bg-white rounded-xl border border-[#E6E2D6] shadow-sm hover:shadow-lg transition-shadow ring-4 ${ringColor} flex flex-col`}
    >
      <div className={`bg-gradient-to-r ${gradient} text-white rounded-t-xl px-5 py-4 flex items-center gap-3`}>
        <div className="w-10 h-10 rounded-md bg-white/15 flex items-center justify-center">
          <Icon size={20} strokeWidth={1.5} />
        </div>
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/80 font-semibold">{eyebrow}</div>
          <div className="font-serif text-xl leading-tight">{title}</div>
        </div>
      </div>
      <div className="p-4 flex-1">
        {list.length === 0 ? (
          <div className="text-sm text-[#4D5A52] text-center py-10">{empty}</div>
        ) : (
          <ul className="divide-y divide-[#F4E9D6]">
            {list.map((it) => (
              <li key={it.id} className="py-3 flex items-start gap-3" data-testid={`${testid}-item-${it.id}`}>
                <FileText size={14} strokeWidth={1.5} className="text-[#F4751F] mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#0F1F17] line-clamp-2 flex items-start gap-1.5">
                    <span className="flex-1">{it.name}</span>
                    {isNew(it) && (
                      <span
                        data-testid={`${testid}-new-badge-${it.id}`}
                        className="shrink-0 inline-flex items-center text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded-full bg-[#F4751F] text-white animate-pulse"
                      >
                        New
                      </span>
                    )}
                  </div>
                  {(it.pdf_url || it.external_url) && (
                    <a
                      href={it.pdf_url || it.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-0.5 inline-flex items-center gap-1 text-xs text-[#1F6B3F] hover:text-[#13532E] font-medium"
                    >
                      Open <ExternalLink size={10} strokeWidth={1.5} />
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>    </div>
  );
}
